import React, {ReactNode, useCallback, useEffect, useRef, useState} from "react";
import {alpha, Box} from "@mui/material";
import {Theme, useTheme} from "@mui/material/styles";
import {SxProps} from "@mui/system";

interface ScrollFaderProps {
    children: ReactNode;
    height?: string | number;
    width?: string | number;
    direction?: "vertical" | "horizontal" | "both";
    color?: "primary" | "secondary";
    faderSize?: number | string;
    deps?: React.DependencyList;
    sx?: SxProps<Theme>;
    contentSx?: SxProps<Theme>;
}

const ScrollFader: React.FC<ScrollFaderProps> = ({
                                                     children,
                                                     height = "100%",
                                                     width = "100%",
                                                     direction = "vertical",
                                                     color = "secondary",
                                                     faderSize = 20,
                                                     deps = [],
                                                     sx,
                                                     contentSx,
                                                 }) => {
    // Opacity states for each fader
    const [topOpacity, setTopOpacity] = useState(0);
    const [bottomOpacity, setBottomOpacity] = useState(0);
    const [leftOpacity, setLeftOpacity] = useState(0);
    const [rightOpacity, setRightOpacity] = useState(0);

    const scrollRef = useRef<HTMLDivElement | null>(null);
    const theme = useTheme();

    const handleScroll = useCallback(() => {
        const scrollElement = scrollRef.current;
        if (scrollElement) {
            const {
                scrollTop,
                scrollHeight,
                clientHeight,
                scrollLeft,
                scrollWidth,
                clientWidth,
            } = scrollElement;

            // Determine fade distance in pixels
            const fadeSize =
                typeof faderSize === "number" ? faderSize : parseInt(faderSize.toString(), 10) || 20;

            // Vertical fading logic
            if (direction === "vertical" || direction === "both") {
                // Top fader: fade in gradually from the top edge
                const newTopOpacity = scrollTop < fadeSize ? scrollTop / fadeSize : 1;
                // Bottom fader: fade out when approaching the bottom
                const scrollable = scrollHeight - clientHeight;
                const scrollEnd = scrollable - fadeSize;
                const newBottomOpacity = scrollTop < scrollEnd ? 1 : (scrollable - scrollTop) / fadeSize;
                setTopOpacity(newTopOpacity);
                setBottomOpacity(newBottomOpacity);
            }

            // Horizontal fading logic
            if (direction === "horizontal" || direction === "both") {
                // Left fader: fade in gradually from the left edge
                const newLeftOpacity = scrollLeft < fadeSize ? scrollLeft / fadeSize : 1;
                // Right fader: fade out when approaching the right edge
                const scrollableWidth = scrollWidth - clientWidth;
                const scrollEndX = scrollableWidth - fadeSize;
                const newRightOpacity = scrollLeft < scrollEndX ? 1 : (scrollableWidth - scrollLeft) / fadeSize;
                setLeftOpacity(newLeftOpacity);
                setRightOpacity(newRightOpacity);
            }
        }
    }, [direction, faderSize]);

    useEffect(() => {
        let scrollElement: HTMLDivElement | null = null;

        const timeoutId = window.setTimeout(() => {
            scrollElement = scrollRef.current;
            scrollElement?.addEventListener("scroll", handleScroll);
            window.addEventListener("resize", handleScroll);
            handleScroll();
        }, 200);

        return () => {
            window.clearTimeout(timeoutId);
            scrollElement?.removeEventListener("scroll", handleScroll);
            window.removeEventListener("resize", handleScroll);
        };
    }, [direction, handleScroll, deps]);

    return (
        <Box
            sx={[
                {
                    maxHeight: height,
                    width,
                    position: "relative",
                    overflow: "hidden", // Prevents content overflow
                },
                ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
            ]}
        >
            {/* Scrollable Content */}
            <Box
                ref={scrollRef}
                sx={[
                    {
                        maxHeight: height,
                        width: "100%",
                        overflowY: direction === "horizontal" ? "hidden" : "auto",
                        overflowX: direction === "vertical" ? "hidden" : "auto",
                    },
                    ...(Array.isArray(contentSx) ? contentSx : contentSx ? [contentSx] : []),
                ]}
            >
                {children}
            </Box>

            {/* Vertical faders */}
            {(direction === "vertical" || direction === "both") && (
                <>
                    {/* Top Fader */}
                    <Box
                        sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            height: faderSize,
                            background: `linear-gradient(to bottom, ${alpha(
                                theme.palette[color].main,
                                0.7
                            )}, transparent)`,
                            pointerEvents: "none",
                            opacity: topOpacity,
                            transition: "opacity 0.3s ease",
                        }}
                    />
                    {/* Bottom Fader */}
                    <Box
                        sx={{
                            position: "absolute",
                            bottom: 0,
                            left: 0,
                            right: 0,
                            height: faderSize,
                            background: `linear-gradient(to top, ${alpha(
                                theme.palette[color].main,
                                0.7
                            )}, transparent)`,
                            pointerEvents: "none",
                            opacity: bottomOpacity,
                            transition: "opacity 0.3s ease",
                        }}
                    />
                </>
            )}

            {/* Horizontal faders */}
            {(direction === "horizontal" || direction === "both") && (
                <>
                    {/* Left Fader */}
                    <Box
                        sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            bottom: 0,
                            width: faderSize,
                            background: `linear-gradient(to right, ${alpha(
                                theme.palette[color].main,
                                0.7
                            )}, transparent)`,
                            pointerEvents: "none",
                            opacity: leftOpacity,
                            transition: "opacity 0.3s ease",
                        }}
                    />
                    {/* Right Fader */}
                    <Box
                        sx={{
                            position: "absolute",
                            top: 0,
                            right: 0,
                            bottom: 0,
                            width: faderSize,
                            background: `linear-gradient(to left, ${alpha(
                                theme.palette[color].main,
                                0.7
                            )}, transparent)`,
                            pointerEvents: "none",
                            opacity: rightOpacity,
                            transition: "opacity 0.3s ease",
                        }}
                    />
                </>
            )}
        </Box>
    );
};

export default ScrollFader;
